import { useEffect, useState } from "react";
import "./App.css";
import Box from "@mui/material/Box";
import CustomContainer from "./components/CustomContainer";
import {
  CircularProgress,
  CssBaseline,
  Paper,
  Skeleton,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import theme from "./utils/theme";
import hardcodedSiteConfig from "./utils/hardcodedSiteConfig";
import ProgressStatus from "./components/ProgressStatus";

function App() {
  const [siteConfig, setSiteConfig] = useState({});
  const [data, setData] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoadingStatus(true);
      const searchParams = new URLSearchParams(window.location.search);
      const paramObj = Object.fromEntries(searchParams.entries());

      if (!paramObj || Object.keys(paramObj).length === 0) {
        setData({ Status: "Niet gevonden..." });
        setLoadingStatus(false);
        return;
      }

      try {
        const res = await fetch(process.env.REACT_APP_DATA_WEBHOOK, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paramObj),
        });

        if (!res.ok) throw new Error("Response not ok");

        const data = await res.json();
        setData(data);
      } catch (error) {
        console.error("Fout bij laden data:", error);
        setData({ Status: "Niet gevonden" });
      } finally {
        setLoadingStatus(false);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(hardcodedSiteConfig).length > 0) {
      setSiteConfig(hardcodedSiteConfig);
      setLoadingConfig(false);
      return;
    }

    const localConfig = localStorage.getItem("siteConfig");

    if (localConfig) {
      setSiteConfig(JSON.parse(localConfig));
      setLoadingConfig(false);
    } else {
      const fetchConfig = async () => {
        try {
          const res = await fetch(process.env.REACT_APP_SITE_CONFIG_WEBHOOK);
          if (!res.ok) throw new Error("Response not ok");
          const config = await res.json();
          setSiteConfig(config);
          localStorage.setItem("siteConfig", JSON.stringify(config));
        } catch (error) {
          console.error("Fout bij laden config:", error);
        } finally {
          setLoadingConfig(false);
        }
      };
      fetchConfig();
    }
  }, []);

  useEffect(() => {
    const pageTitle = siteConfig.pageTitle;

    if (pageTitle) {
      document.title = pageTitle;
    }
  }, [siteConfig]);

  if (loadingConfig)
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <CircularProgress color={"lightGrey"} />
      </Box>
    );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div
        className="App"
        style={{
          backgroundColor: siteConfig.backgroundColor,
          height: "100vh",
        }}
      >
        <CustomContainer
          title={siteConfig.containerTitle || "Status"}
          siteConfig={siteConfig}
        >
          {loadingStatus ? (
            <Stack spacing={1}>
              {[50, 55, 35, 45, 50].map((width, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  sx={{
                    fontSize: "1.3rem",
                    bgcolor: "#a1a1a1",
                    width: `${width}%`,
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Stack spacing={1}>
              {data.details &&
                Object.entries(data.details).map(([key, value]) => (
                  <Typography
                    key={key}
                    sx={{ color: siteConfig.smallContainerTextColor }}
                  >
                    <strong>{key}:</strong> {value}
                  </Typography>
                ))}
            </Stack>
          )}

          <Typography
            sx={{
              textAlign: "center",
              mt: 2,
              color: " #908d88",
            }}
          >
            Laatste statusupdate van je bestelling:
          </Typography>
          <Paper
            sx={{
              backgroundColor: siteConfig.statusBarColor || "#6a21ff",
              color: siteConfig.statusBarTextColor || "#fff",
              height: 75,
              mt: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            {loadingStatus ? (
              <Skeleton
                variant="text"
                sx={{ fontSize: "4rem", width: "95%" }}
              />
            ) : (
              <Typography
                variant={"h2"}
                sx={{
                  fontSize: {
                    xs: "1.5rem",
                    sm: "2rem",
                  },
                }}
              >
                {data.Status}
              </Typography>
            )}
          </Paper>
          <ProgressStatus statusId={data.StatusId} />
        </CustomContainer>
      </div>
    </ThemeProvider>
  );
}

export default App;
